import { useState, useRef, useEffect } from "react";
import { Reveal, SectionLabel, LuxBg } from "../Reveal";
import { GoldSeal } from "../effects/GoldSeal";
import { Accordion } from "../Accordion";
import { HOST, ASSETS } from "../../content";
import { REPLAY_BIO, OBJECTION_PARAGRAPHS, BOOKING_BULLETS, REPLAY_FAQS, REPLAY_TRUST, GHL_CALENDAR_URL } from "../../replayContent";

const Corner = ({ pos }) => <span className={`absolute w-5 h-5 border-[#FE0000] ${pos}`} aria-hidden="true" />;

export const ReplayHost = () => {
  const [hovered, setHovered] = useState(false);
  const [mouse, setMouse] = useState({ x: 0, y: 0 });
  const [isDesktop, setIsDesktop] = useState(true);
  const panelRef = useRef(null);

  useEffect(() => {
    const mq = window.matchMedia("(min-width: 768px) and (hover: hover)");
    setIsDesktop(mq.matches);
    const fn = (e) => setIsDesktop(e.matches);
    mq.addEventListener("change", fn);
    return () => mq.removeEventListener("change", fn);
  }, []);

  const onMove = (e) => {
    const rect = panelRef.current.getBoundingClientRect();
    setMouse({ x: ((e.clientX - rect.left) / rect.width - 0.5) * 2, y: ((e.clientY - rect.top) / rect.height - 0.5) * 2 });
  };

  return (
    <section id="host" className="relative py-28 md:py-36 px-6 sm:px-10 lg:px-16 xl:px-24" data-testid="replay-host-section">
      <LuxBg n={3} />
      <Reveal><SectionLabel num="004" text="YOUR HOST" /></Reveal>
      <div className="grid lg:grid-cols-2 gap-14 lg:gap-20 mt-4">
        <Reveal>
          <div
            ref={panelRef}
            onMouseEnter={() => isDesktop && setHovered(true)}
            onMouseLeave={() => setHovered(false)}
            onMouseMove={isDesktop ? onMove : undefined}
            className={`relative w-full max-w-md mx-auto lg:mx-0 rounded-lg bg-[#182f56] border border-white/10 overflow-hidden ${!isDesktop ? "pulse-border" : ""}`}
            style={{ aspectRatio: "4/5" }}
            data-testid="replay-host-panel"
          >
            <Corner pos="top-3 left-3 border-t border-l" />
            <Corner pos="top-3 right-3 border-t border-r" />
            <Corner pos="bottom-3 left-3 border-b border-l" />
            <Corner pos="bottom-3 right-3 border-b border-r" />
            <div
              className="absolute inset-0 transition-opacity duration-500 pointer-events-none"
              style={{ opacity: hovered ? 1 : 0, background: "radial-gradient(circle at 50% 55%, rgba(254,0,0,0.22) 0%, rgba(254,0,0,0.05) 45%, transparent 70%)" }}
            />
            {isDesktop && <GoldSeal hovered={hovered} mouse={mouse} />}
            <div className="absolute inset-0 transition-opacity duration-500" style={{ opacity: hovered ? 0 : 1 }}>
              <img src={ASSETS.headshot} alt={HOST.name} className="absolute inset-0 w-full h-full object-cover" data-testid="replay-host-headshot" />
            </div>
          </div>
        </Reveal>
        <div>
          <Reveal delay={0.05}>
            <h2 className="serif font-black uppercase tracking-tight text-3xl md:text-4xl text-white">
              ONE ADVOCATE FOR YOUR FINANCING. <span className="text-[#FE0000]">AND YOUR NEXT MOVE.</span>
            </h2>
            <div className="h-px w-24 bg-[#FE0000] my-8" />
          </Reveal>
          <div className="space-y-5">
            {REPLAY_BIO.map((p, i) => (
              <Reveal key={i} delay={0.08 + i * 0.05}>
                <p className="text-white text-[17px] leading-[1.8]" data-testid={`replay-bio-${i}`}>{p}</p>
              </Reveal>
            ))}
          </div>
          <Reveal delay={0.2}>
            <div className="grid grid-cols-2 gap-px bg-[#20416F]/40 border border-[#20416F] rounded-lg overflow-hidden mt-10" data-testid="replay-credential-grid">
              {[["COMPANY", "US Lending Partner PRMG"], ["NMLS#", HOST.nmls], ["CO. NMLS#", HOST.companyNmls], ["LICENSED IN", HOST.states]].map(([label, value]) => (
                <div key={label} className="bg-[#0c0f16] p-5">
                  <div className="mono text-[13px] tracking-[0.3em] text-white mb-2">{label}</div>
                  <div className="text-base font-semibold text-white">{value}</div>
                </div>
              ))}
            </div>
          </Reveal>
          <Reveal delay={0.25}>
            <button
              data-testid="replay-host-cta"
              data-cta
              onClick={() => document.getElementById("book")?.scrollIntoView({ behavior: "smooth" })}
              className="mt-10 bg-[#FFE100] text-[#12264A] font-bold text-sm uppercase tracking-wide px-8 py-4 rounded-md btn-slide"
            >
              ✓ BOOK MY FREE PRE-APPROVAL REVIEW
            </button>
          </Reveal>
        </div>
      </div>
    </section>
  );
};

export const Objections = () => (
  <section className="py-28 md:py-36 px-6 sm:px-10 lg:px-16 xl:px-24" data-testid="objections-section">
    <div className="grid lg:grid-cols-2 gap-14 lg:gap-24">
      <div>
        <Reveal><SectionLabel num="005" text="LAST DOUBTS" /></Reveal>
        <Reveal delay={0.05}>
          <h2 className="serif font-black uppercase tracking-tight text-3xl md:text-4xl text-white mb-8">
            STILL ON <span className="text-[#FE0000]">THE FENCE?</span>
          </h2>
        </Reveal>
        {OBJECTION_PARAGRAPHS.map((p, i) => (
          <Reveal key={i} delay={0.1 + i * 0.06}>
            <p className="text-white text-[17px] leading-[1.8] mb-5" data-testid={`objection-paragraph-${i}`}>{p}</p>
          </Reveal>
        ))}
      </div>
      <div className="lg:pt-16">
        <Reveal delay={0.1}>
          <div className="rounded-lg border border-[#FE0000]/25 bg-[#FE0000]/[0.04] p-8">
            <div className="mono text-[13px] tracking-[0.3em] text-[#FFC631] mb-8">HERE&apos;S WHAT HAPPENS WHEN YOU BOOK:</div>
            <ul className="space-y-6">
              {BOOKING_BULLETS.map((t, i) => (
                <li key={i} className="flex gap-4 text-[17px] text-white leading-relaxed" data-testid={`booking-bullet-${i}`}>
                  <span className="text-[#FE0000] font-bold shrink-0">→</span>
                  {t}
                </li>
              ))}
            </ul>
          </div>
        </Reveal>
      </div>
    </div>
  </section>
);

export const ReplayFaq = () => (
  <section id="faq" className="relative py-28 md:py-36 px-6 sm:px-10 lg:px-16 xl:px-24" data-testid="replay-faq-section">
    <LuxBg n={1} />
    <div className="max-w-4xl">
      <Reveal><SectionLabel num="006" text="STILL DECIDING?" /></Reveal>
      <Reveal delay={0.05}>
        <h2 className="serif font-black uppercase tracking-tight text-3xl md:text-5xl text-white mb-12">
          COMMON <span className="text-[#FE0000]">QUESTIONS</span>
        </h2>
      </Reveal>
      <Reveal delay={0.1}>
        <Accordion prefix="replay-faq" items={REPLAY_FAQS.map((f) => ({ title: f.q, body: f.a }))} />
      </Reveal>
    </div>
  </section>
);

export const BookingSection = ({ attribution, watchPct, defaultEmail }) => (
  <section id="book" className="relative py-28 md:py-36 px-6 sm:px-10 lg:px-16 xl:px-24 overflow-hidden bg-white/[0.015]" data-testid="booking-section">
    <div
      className="absolute left-0 top-1/2 -translate-y-1/2 w-[700px] h-[700px] pointer-events-none"
      style={{ background: "radial-gradient(circle, rgba(254,0,0,0.10) 0%, transparent 65%)" }}
      aria-hidden="true"
    />
    <div className="relative grid lg:grid-cols-2 gap-14 lg:gap-20 items-center">
      <div>
        <Reveal><SectionLabel num="007" text="BOOK YOUR REVIEW" /></Reveal>
        <Reveal delay={0.05}>
          <h2 className="serif font-black uppercase tracking-[-0.02em] text-[clamp(36px,4.4vw,60px)] text-white" style={{ lineHeight: 0.97 }} data-testid="booking-h2">
            ANCHOR YOUR FINANCING. <span className="text-[#FE0000]">ASCEND INTO YOUR NEXT MOVE.</span>
          </h2>
        </Reveal>
        <div className="mt-10 space-y-4">
          {REPLAY_TRUST.map((t, i) => (
            <Reveal key={i} delay={0.1 + i * 0.05}>
              <div className="flex gap-4 text-[17px] text-white" data-testid={`replay-trust-${i}`}>
                <span className="text-[#FE0000] font-bold shrink-0">✓</span>
                {t}
              </div>
            </Reveal>
          ))}
        </div>
      </div>
      <Reveal delay={0.15}>
        <div className="rounded-xl border border-white/10 bg-[#132441]/75 backdrop-blur-xl p-6 sm:p-8 max-w-md mx-auto lg:ml-auto w-full" data-testid="booking-card">
          <div className="mono text-[13px] tracking-[0.35em] text-[#FFC631]">FREE VA ELIGIBILITY &amp; PRE-APPROVAL REVIEW</div>
          <h3 className="serif text-xl font-black uppercase tracking-tight text-white mt-3 mb-5">Pick Your Time &amp; Book</h3>
          {GHL_CALENDAR_URL && (
            <div className="rounded-lg overflow-hidden border border-white/10" data-testid="ghl-calendar-embed">
              <iframe src={GHL_CALENDAR_URL} title="Booking Calendar" className="w-full" style={{ height: 620, border: "none" }} />
            </div>
          )}
          <p className="mono text-[13px] text-white text-center mt-4 tracking-wider">
            Free · No hard credit pull · One advocate for financing and representation
          </p>
        </div>
      </Reveal>
    </div>
  </section>
);
